import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { type AuditEventDetail } from '../types';

interface AuditEventDetailDrawerProps {
 event: AuditEventDetail | null;
 open: boolean;
 onClose: () => void;
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
 day: '2-digit',
 month: 'short',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 hour12: false,
});

export function AuditEventDetailDrawer({ event, open, onClose }: AuditEventDetailDrawerProps) {
 if (!event) return null;

 return (
 <div
 className={cn(
 'fixed inset-0 z-modal flex justify-end transition-opacity duration-300',
 open ? 'opacity-100' : 'opacity-0 pointer-events-none'
 )}
 >
 <div
 className="absolute inset-0 bg-black/50"
 onClick={onClose}
 aria-hidden="true"
 />
 <div
 className={cn(
 'relative w-full max-w-lg bg-surface-1 border-l border-border h-full shadow-2xl flex flex-col transition-transform duration-300 transform',
 open ? 'translate-x-0' : 'translate-x-full'
 )}
 >
 <div className="flex items-center justify-between p-6 border-b border-border">
 <div>
 <h2 className="text-lg font-semibold text-text-primary">Event Details</h2>
 <p className="text-xs text-text-tertiary font-mono mt-1">{event.id}</p>
 </div>
 <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close drawer">
 ×
 </Button>
 </div>

 <div className="flex-1 overflow-y-auto p-6 space-y-6">
 <section className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">Occurred At</label>
 <p className="text-sm text-text-primary mt-0.5 font-medium">
 {dateFormatter.format(new Date(event.occurredAt))}
 </p>
 </div>
 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">Event Type</label>
 <div className="mt-1">
 <StatusBadge status="info" label={event.eventType} dot={false} className="font-mono text-caption" />
 </div>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">Actor</label>
 <p className="text-sm text-text-primary mt-0.5 font-mono">
 {event.actorId || 'System'}
 </p>
 <p className="text-caption text-text-tertiary uppercase">{event.actorAudience}</p>
 </div>
 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">IP Address</label>
 <p className="text-sm text-text-primary mt-0.5">{event.ipAddress || '-'}</p>
 </div>
 </div>

 {(event.resourceType || event.resourceId) && (
 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">Resource</label>
 <div className="flex items-center gap-2 mt-1">
 <span className="text-sm text-text-primary font-medium">{event.resourceType}</span>
 <span className="text-xs text-text-tertiary font-mono">{event.resourceId}</span>
 </div>
 </div>
 )}

 <div>
 <label className="text-caption uppercase text-text-tertiary font-medium">User Agent</label>
 <p className="text-xs text-text-secondary mt-1 break-words">{event.userAgent || '-'}</p>
 </div>
 </section>

 <section className="space-y-2">
 <label className="text-caption uppercase text-text-tertiary font-medium">Payload</label>
 <div className="bg-surface-2 border border-border rounded-[8px] p-4 overflow-x-auto">
 <pre className="text-xs text-text-primary font-mono leading-relaxed">
 {JSON.stringify(event.payload, null, 2)}
 </pre>
 </div>
 </section>
 </div>

 <div className="p-6 border-t border-border bg-surface-2 flex justify-end gap-3">
 <Button variant="ghost" size="md" onClick={onClose}>
 Close
 </Button>
 {event.resourceType && event.resourceId && (
 <Button 
 variant="primary" 
 size="md"
 onClick={() => {
 window.location.href = `/audit/resource/${event.resourceType}/${event.resourceId}`;
 }}
 >
 View Resource History
 </Button>
 )}
 </div>
 </div>
  </div>
 );
}
